/**
 * ProjectNode — Single Project Star on the Cosmic Map
 * ────────────────────────────────────────────────────
 * Extracted from CosmicMap for independent testability and to slim
 * the main component. Renders the circle node with:
 *   - Cover image or fallback letter
 *   - Type-colored ring (physical/digital/hybrid)
 *   - Selection/highlight glow animations
 *   - Multi-select checkmark ring
 *   - Proximity bloom scaling
 *   - Fork count badge
 *   - Idle drift animation class
 *   - Label below (outside bloom wrapper so text doesn't scale)
 *
 * v3 Changes — Zoom-Reveal Disambiguation:
 *   - Accepts a `zoom` prop (default 1) and uses hitRadiusForZoom() to
 *     keep the invisible hit-circle at a constant *screen pixel* size
 *     regardless of the current map zoom level.
 *   - At default zoom the hit area is identical to before.
 *   - Zooming in shrinks the SVG-space hit radius inversely, so adjacent
 *     nodes that overlap at zoom 1.0 become individually clickable as
 *     the user leans in — no new UI, no mystery behavior.
 *   - Backwards-compatible: if `zoom` is not passed, behavior is unchanged.
 *
 * Place at: src/components/map/ProjectNode.jsx
 */

import React from 'react';
import { PROJECT_TYPES } from '../../config/zones';
import {
  NODE_SIZE, NODE_SIZE_MAX, NODE_HIT_AREA,
  BLOOM_EASE, BLOOM_DURATION, lerp, hitRadiusForZoom,
} from './mapConstants';

const ProjectNode = ({
  node, isSelected, isHighlighted, isDimmed, isMultiSelected, isMultiSelectMode,
  bloomFactor, hitRadius, zoom = 1,
  isWorking = false, hasLiveSession = false,
  lensWarmth = 0, isLensProbe = false,
  onClick, onDoubleClick, onMouseEnter, onMouseLeave,
  onPointerDown, onPointerUp,
}) => {
  const project = node.project;
  const zone = node.zone;
  const projectType = PROJECT_TYPES[project.projectType || project.project_type || 'digital'];

  const bf = bloomFactor || 0;
  const bloomScale = lerp(1, NODE_SIZE_MAX / NODE_SIZE, bf);
  const halfSize = NODE_SIZE / 2;
  const clipR = halfSize - 2;

  // Hit radius: zoom-aware so the clickable target stays at a constant
  // *screen* size. hitRadius prop (if provided) is treated as a
  // screen-pixel diameter; we shrink it in SVG space as zoom increases.
  const baseHitArea = hitRadius || NODE_HIT_AREA;
  const effectiveHitR = hitRadiusForZoom(baseHitArea, zoom);

  const clipId = `clip-${node.id}`;

  return (
    <g
      transform={`translate(${node.x}, ${node.y})`}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onClick?.(node.project, e); }}
      onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick?.(node.project); }}
      onPointerDown={(e) => { onPointerDown?.(); }}
      onPointerUp={(e) => { onPointerUp?.(); }}
      onPointerCancel={(e) => { onPointerUp?.(); }}
      onMouseEnter={(e) => onMouseEnter?.(node, e.nativeEvent)}
      onMouseLeave={() => onMouseLeave?.()}
      className={bf < 0.05 ? `idle-drift-${['a','b','c'][Math.abs(node.id?.charCodeAt?.(0) || 0) % 3]}` : undefined}
      style={{ cursor: 'pointer', opacity: isDimmed ? 0.08 : 1, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
    >
      {/* Live session pulse — emerald expanding rings */}
        {hasLiveSession && (
          <>
            <circle r={halfSize + 18} fill="none" stroke="#10b981" strokeWidth={1.5} opacity={0}>
              <animate attributeName="r" values={`${halfSize + 6};${halfSize + 26};${halfSize + 6}`} dur="2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.6;0;0.6" dur="2s" repeatCount="indefinite" />
            </circle>
            <circle r={halfSize + 10} fill="none" stroke="#10b981" strokeWidth={1} opacity={0}>
              <animate attributeName="r" values={`${halfSize + 4};${halfSize + 18};${halfSize + 4}`} dur="2s" begin="0.4s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.4;0;0.4" dur="2s" begin="0.4s" repeatCount="indefinite" />
            </circle>
            <circle r={halfSize + 2} fill="#10b981" opacity={0.12}>
              <animate attributeName="opacity" values="0.08;0.18;0.08" dur="1.5s" repeatCount="indefinite" />
            </circle>
          </>
        )}

        {/* Working on it — amber slow pulse */}
        {isWorking && !hasLiveSession && (
          <>
            <circle r={halfSize + 12} fill="none" stroke="#f59e0b" strokeWidth={1} opacity={0.2}>
              <animate attributeName="r" values={`${halfSize + 8};${halfSize + 16};${halfSize + 8}`} dur="3s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.25;0.05;0.25" dur="3s" repeatCount="indefinite" />
            </circle>
            <circle r={halfSize + 2} fill="#f59e0b" opacity={0.08}>
              <animate attributeName="opacity" values="0.05;0.14;0.05" dur="3s" repeatCount="indefinite" />
            </circle>
          </>
        )}

{/* Lens warmth glow — bold fuchsia heat map, proportional to match strength */}
       {lensWarmth > 0 && !isDimmed && (
         <>
           {/* Outer bloom — large, soft */}
           <circle r={halfSize + 20 + lensWarmth * 15} fill="#d946ef" opacity={lensWarmth * 0.2}>
             <animate attributeName="opacity" values={`${lensWarmth * 0.12};${lensWarmth * 0.28};${lensWarmth * 0.12}`} dur="3s" repeatCount="indefinite" />
           </circle>
           {/* Inner glow — tight, bright */}
           <circle r={halfSize + 6} fill="#d946ef" opacity={0.1 + lensWarmth * 0.25} />
           {/* Ring — crisp edge */}
           <circle r={halfSize + 3} fill="none" stroke="#d946ef" strokeWidth={1.5 + lensWarmth * 2} opacity={0.4 + lensWarmth * 0.5} />
         </>
       )}

       {/* Lens probe indicator — the anchor node pulses distinctly */}
       {isLensProbe && (
         <>
           <circle r={halfSize + 20} fill="none" stroke="#d946ef" strokeWidth={2} opacity={0.3} strokeDasharray="6 4">
             <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="8s" repeatCount="indefinite" />
           </circle>
           <circle r={halfSize + 12} fill="#d946ef" opacity={0.08}>
             <animate attributeName="opacity" values="0.05;0.15;0.05" dur="2s" repeatCount="indefinite" />
           </circle>
         </>
       )}


      {/* Bloom wrapper */}
      <g style={{
        transform: `scale(${bloomScale})`,
        transition: `transform ${BLOOM_DURATION} ${BLOOM_EASE}`,
        transformOrigin: '0 0',
      }}>
        {/* Invisible hit area — zoom-aware so overlapping nodes become
            individually clickable when the user zooms in. */}
        <circle r={effectiveHitR} fill="transparent" />

        {/* Multi-select ring */}
        {isMultiSelected && (
          <>
            <circle r={halfSize + 16} fill="none" stroke="#22c55e" strokeWidth={3} strokeDasharray="6 3">
              <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="12s" repeatCount="indefinite" />
            </circle>
            <circle r={halfSize + 10} fill="#22c55e" opacity={0.15} />
          </>
        )}

        {/* Selection/highlight glow */}
        {(isSelected || isHighlighted) && !isMultiSelected && (
          <>
            <circle r={halfSize + 14} fill={zone.color} opacity={0.1}>
              <animate attributeName="opacity" values="0.06;0.15;0.06" dur="3s" repeatCount="indefinite" />
            </circle>
            <circle r={halfSize + 7} fill="none" stroke={zone.color} strokeWidth={1.5} opacity={0.5} />
            {isSelected && (
              <circle r={halfSize + 20} fill="none" stroke={zone.color} strokeWidth={0.5} opacity={0.15}>
                <animate attributeName="r" values={`${halfSize+18};${halfSize+24};${halfSize+18}`} dur="2.5s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.15;0.04;0.15" dur="2.5s" repeatCount="indefinite" />
              </circle>
            )}
          </>
        )}

        {/* Bloom glow */}
        {bf > 0.05 && !isDimmed && (
          <circle r={halfSize + 8} fill={zone.color} opacity={bf * 0.15} />
        )}

        {/* Main circle background */}
        <circle
          r={halfSize}
          fill="#18181b"
          stroke={projectType?.color || '#52525b'}
          strokeWidth={isSelected || isHighlighted ? 2.5 : bf > 0.3 ? 2 : 1.5}
          opacity={isSelected || isHighlighted ? 1 : 0.85}
        />

        {/* Clip path */}
        <defs>
          <clipPath id={clipId}>
            <circle r={clipR} />
          </clipPath>
        </defs>

        {/* Cover image or fallback letter */}
        {project.coverImage || project.cover_image || project.image_url ? (
          <image
            href={project.coverImage || project.cover_image || project.image_url}
            x={-clipR}
            y={-clipR}
            width={clipR * 2}
            height={clipR * 2}
            clipPath={`url(#${clipId})`}
            preserveAspectRatio="xMidYMid slice"
          />
        ) : (
          <text textAnchor="middle" dominantBaseline="central" fill="#a1a1aa" fontSize="14" fontWeight="600">
            {project.name?.[0]?.toUpperCase() || '?'}
          </text>
        )}

        {/* Multi-select checkmark */}
        {isMultiSelected && (
          <g transform={`translate(14, -14)`}>
            <circle r={10} fill="#22c55e" />
            <path d="M-4 0 L-1 3 L4 -3" fill="none" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </g>
        )}

        {/* Live session dot — top-left */}
        {hasLiveSession && !isMultiSelected && (
          <g transform={`translate(${-halfSize + 5}, ${-halfSize + 5})`}>
            <circle r={6} fill="#18181b" />
            <circle r={4.5} fill="#10b981">
              <animate attributeName="opacity" values="1;0.5;1" dur="1.2s" repeatCount="indefinite" />
            </circle>
          </g>
        )}

        {/* Working dot — top-left (dimmer, shown when not live) */}
        {isWorking && !hasLiveSession && !isMultiSelected && (
          <g transform={`translate(${-halfSize + 5}, ${-halfSize + 5})`}>
            <circle r={5} fill="#18181b" />
            <circle r={3.5} fill="#f59e0b" opacity={0.9}>
              <animate attributeName="opacity" values="0.9;0.4;0.9" dur="2.5s" repeatCount="indefinite" />
            </circle>
          </g>
        )}

        {/* Fork count badge */}
        {(project.forkCount || project.fork_count) > 0 && !isMultiSelected && (
          <g transform={`translate(${halfSize - 6}, ${-halfSize + 6})`}>
            <circle r={9} fill="#7c3aed" />
            <text textAnchor="middle" dominantBaseline="central" fill="white" fontSize="9" fontWeight="bold">
              {project.forkCount || project.fork_count}
            </text>
          </g>
        )}
      </g>

      {/* Label sits OUTSIDE the bloom scale wrapper so text doesn't scale up */}
      <text
        y={halfSize * bloomScale + 14}
        textAnchor="middle"
        fill={isMultiSelected ? '#22c55e' : isSelected ? zone.color : '#71717a'}
        fontSize="10"
        fontWeight={isSelected || isMultiSelected ? '600' : '400'}
        style={{ pointerEvents: 'none' }}
      >
        {project.name?.length > 14 ? project.name.slice(0, 14) + '\u2026' : project.name}
      </text>
    </g>
  );
};

export default React.memo(ProjectNode);